// Types and wording for the Lateness page (db/migrations/1100 to 1105).
//
// NOTHING HERE DECIDES ANYTHING. Who was late, by how much, whether a manager
// must rule first, whether a notice excuses, what the ladder gives and what to
// do next all arrive from lateness_page_v1 (CLAUDE.md rule 4). This file turns
// codes into words.
//
// A MISSING FIGURE SHOWS AS MISSING. Minutes Cartrack could not measure are
// '—', never 0, because "0 minutes late" would say the driver was on time
// (CLAUDE.md rule 6).

export type Step = 'verbal_warning' | 'written_warning' | 'final_written_warning' | 'termination'
export type CaseStatus = 'raised' | 'notice_issued' | 'response_received' | 'hearing_referred' | 'decided' | 'withdrawn'
export type WarningStatus = 'awaiting_signature' | 'signed' | 'refused_awaiting_witness' | 'refused_witnessed' | 'paper_signed'

export type Handover = {
  incoming_shift_id: string
  date: string
  time: string
  plate: string
  outgoing: string
  incoming: string
  late_role: 'outgoing' | 'incoming' | null
  late_driver: string | null
  late_driver_id: string | null
  minutes_late: number | null
  form_bucket: string | null
  form_partner_name: string | null
  form_partner_matches: boolean | null
  borderline: boolean
  cartrack_status: string | null
  cartrack_minutes: number | null
  cartrack_arrival: string | null
  cartrack_collected: string | null
  disagree: boolean
  evidence_source: string | null
  needs_review: boolean
  review_decision: string | null
  review_note: string | null
  notice: { id: string; at: string; before: boolean; reason: string; valid: boolean; confirmation: string | null } | null
  excused: boolean
  notice_pending: boolean
  absence: boolean
  absence_possible: boolean
  step_eligible: boolean
  coaching_only_reason: string | null
  unpaid_minutes: number | null
  paid_wait_minutes: number | null
}

export type Fairness = {
  partner_late_same_week: { found: boolean; rows: { date: string; handover: string; partner: string; minutes: number }[] }
  vehicle_breakdown_or_service: { found: boolean; rows: string[] }
  fleetly_paid_late: { found: boolean | null; rows: { pay_month: string; released_on: string }[]; note: string | null }
}

export type CaseEvidence = {
  incoming_shift_id: string
  shift_date: string
  handover_time: string
  plate: string
  role: string
  minutes_late: number
  partner: string | null
  form_bucket: string | null
  borderline: boolean
  cartrack: string | null
  evidence_source: string | null
  review_note: string | null
}

export type LatenessCase = {
  id: string
  ref: string
  driver: string
  driver_id: string
  day: string
  status: CaseStatus
  step: Step
  step_label: string
  allegation: string
  evidence: CaseEvidence[]
  fairness: Fairness | null
  fairness_considered_at: string | null
  notice_sent: string | null
  notice_method: string | null
  response_due: string | null
  response_window_open: boolean
  response: string | null
  no_response: boolean | null
  response_source: string | null
  outcome: string | null
  outcome_reasons: string | null
  procedure: Record<string, unknown> | null
  warnings: { id: string; sanction: string; status: WarningStatus; given_on: string; lapses_on: string | null; sign_due_on: string }[] | null
  events: { at: string; type: string; detail: string; by: string | null }[] | null
  next_action: string | null
}

export type LatenessPage = {
  week_start: string
  week_end: string
  today: string
  me: { person_id: string | null; is_admin: boolean; can_record_hearing: boolean }
  switches: {
    messages_send: boolean
    signatures_recorded: number
    agreement_in_force: { version: string; in_force_from: string | null }[] | null
  }
  settings: { key: string; value: number; from: string; note: string | null }[]
  this_week: Handover[]
  awaiting_review: Handover[]
  awaiting_review_older: number
  notices_pending: {
    id: string
    driver: string
    driver_id: string
    handover: string
    given: string
    before: boolean
    reason: string
    valid: boolean
    note: string | null
    source: string
    evidence: string | null
    expected_minutes: number | null
  }[]
  cases: LatenessCase[]
  warnings_awaiting_signature: { id: string; driver: string; sanction: string; given_on: string; sign_due_on: string; overdue: boolean; case_id: string }[]
  refusals_awaiting_witness: {
    id: string
    driver: string
    sanction: string
    given_on: string
    kind: string
    note: string
    recorded_by: string
    witness: string
    i_am_witness: boolean
  }[]
  drivers: {
    driver_id: string
    name: string
    next_step: Step
    gate_date: string | null
    late_30: number
    late_90: number
    in_force: { sanction: string; issued_at: string; lapses_on: string | null }[] | null
  }[]
  weeks: { week_start: string; handovers: number; late: number; awaiting_review: number; accounted: number; on_time_rate: number | null }[]
  options: {
    reasons: { code: string; label: string; valid: boolean }[]
    staff: { person_id: string; name: string }[]
  }
  all_drivers: {
    driver_id: string
    name: string
    servant: string | null
    mine: boolean
    has_email: boolean
    link_made: boolean
    link_last_opened: string | null
  }[]
  personLinked: boolean
}

export const STEP_LABEL: Record<string, string> = {
  verbal_warning: 'Verbal warning',
  written_warning: 'Written warning',
  final_written_warning: 'Final written warning',
  termination: 'Dismissal, at a hearing',
}

export const STATUS_LABEL: Record<CaseStatus, string> = {
  raised: 'Record not yet given',
  notice_issued: 'Waiting for his explanation',
  response_received: 'To decide',
  hearing_referred: 'Hearing',
  decided: 'Decided',
  withdrawn: 'Withdrawn or excused',
}

export const WARNING_STATUS_LABEL: Record<WarningStatus, string> = {
  awaiting_signature: 'Waiting for his acknowledgement',
  signed: 'Acknowledged with PIN',
  refused_awaiting_witness: 'Refused, waiting for the witness',
  refused_witnessed: 'Refused, witnessed',
  paper_signed: 'Signed on paper',
}

export const SOURCE_LABEL: Record<string, string> = {
  form_and_cartrack: 'Partner’s form, Cartrack agrees',
  form: 'Partner’s form',
  cartrack: 'Cartrack only',
  manager_review: 'Manager’s ruling',
}

export const CARTRACK_LABEL: Record<string, string> = {
  arrived: 'Seen arriving',
  arrived_unclear: 'Arrival not clear',
  collected: 'Collected from the outgoing driver',
  homes_close: 'Partners live under 1 km apart',
  no_home: 'No home address',
  no_trips: 'No trips',
  not_seen: 'Not seen at either home',
}

export const NOTICE_METHODS: { value: string; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'letter', label: 'Letter' },
  { value: 'in_person_signed', label: 'In person, signed' },
  { value: 'driver_page', label: 'His page (link sent)' },
]

/** Minutes, or the dash that means nobody measured it. */
export function minutes(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : `${n} min`
}

/** Who was late, in a sentence. */
export function lateLine(h: Pick<Handover, 'late_role' | 'late_driver' | 'minutes_late'>): string {
  if (!h.late_role) return 'Not late'
  return `${h.late_driver ?? 'The driver'} (${h.late_role}), ${minutes(h.minutes_late)}`
}

/** The decision choices a Driver Servant is offered, never heavier than the ladder. */
export function decisionChoices(step: Step): { value: string; label: string }[] {
  const out = [
    { value: 'no_action', label: 'No action' },
    { value: 'excused', label: 'Excused, for a clause 6.8 reason' },
    { value: 'verbal_warning', label: 'Verbal warning' },
  ]
  if (step === 'written_warning' || step === 'final_written_warning' || step === 'termination') {
    out.push({ value: 'written_warning', label: 'Written warning (needs the clause 27.4 steps)' })
  }
  if (step === 'final_written_warning' || step === 'termination') {
    out.push({ value: 'refer_to_hearing', label: 'Refer to a hearing' })
  }
  return out
}

/** The outcomes a hearing may record, never heavier than the ladder. */
export function hearingOutcomes(step: Step): { value: string; label: string }[] {
  const all = [
    { value: 'no_action', label: 'No action', rank: 0 },
    { value: 'verbal_warning', label: 'Verbal warning', rank: 1 },
    { value: 'written_warning', label: 'Written warning', rank: 2 },
    { value: 'final_written_warning', label: 'Final written warning', rank: 3 },
    { value: 'termination', label: 'Dismissal', rank: 4 },
  ]
  const top = { verbal_warning: 1, written_warning: 2, final_written_warning: 3, termination: 4 }[step]
  return all.filter((o) => o.rank <= top).map(({ value, label }) => ({ value, label }))
}
